import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../../config';
import { Theme } from '../../ui/theme';
import { GameState } from '../../systems/GameState';
import { OutfitRenderer } from '../../systems/OutfitRenderer';
import { OUTFITS_BY_ID } from '../../data/outfits';
import { svgStringToTexture } from '../../utils/svgToTexture';
import { Button } from '../../ui/Button';
import { ToastManager } from '../../ui/ToastManager';
import { MiniGameRunner } from '../../systems/MiniGameRunner';
import { AudioManager } from '../../systems/AudioManager';
import { CharacterAnimator } from '../../systems/CharacterAnimator';

const ROUND_MS = 30_000;
const BEAT_INTERVAL = 750; // ms per beat (~80 BPM)
const PERFECT_WIN = 80;
const GOOD_WIN = 200;

// Beat gauge: fills from 0 to GAUGE_W over BEAT_INTERVAL ms
const GAUGE_W = 600;
const GAUGE_H = 36;
const GAUGE_X = (GAME_WIDTH - GAUGE_W) / 2;
const GAUGE_Y = GAME_HEIGHT - 200;

export class WalkingTimingScene extends Phaser.Scene {
  private score = 0; private timeLeft = ROUND_MS; private combo = 0;
  private nextBeatAt = 0;
  private characterSprite: Phaser.GameObjects.Image | null = null;
  private scoreText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private comboText!: Phaser.GameObjects.Text;
  private gameTimer!: Phaser.Time.TimerEvent;
  private toast!: ToastManager;
  private ended = false;
  private progressBar!: Phaser.GameObjects.Rectangle;
  // Beat gauge
  private gaugeBar!: Phaser.GameObjects.Rectangle;
  private beatFlash!: Phaser.GameObjects.Rectangle;
  // Countdown text
  private countdownText!: Phaser.GameObjects.Text;
  private showTutorial = true;

  constructor() { super('WalkingTimingScene'); }

  async create(): Promise<void> {
    AudioManager.setScene(this);
    AudioManager.playBgm('rhythm');
    this.cameras.main.setBackgroundColor('#1a0f2e');
    this.toast = new ToastManager(this);
    this.score = 0; this.timeLeft = ROUND_MS; this.combo = 0; this.ended = false;
    this.nextBeatAt = this.time.now + BEAT_INTERVAL * 3; // 3-beat warmup

    // ── Background runway ─────────────────────────────────────────────────
    // Stage floor
    this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT - 150, GAME_WIDTH, 80, 0x6a3a8a);
    // Runway lights
    for (let i = 0; i < 8; i++) {
      const x = 100 + i * 180;
      const light = this.add.circle(x, GAME_HEIGHT - 165, 8, 0xffd95c, 0.8);
      this.tweens.add({
        targets: light, alpha: 0.2, duration: 600 + i * 80,
        yoyo: true, repeat: -1, ease: 'Sine.InOut', delay: i * 100,
      });
    }

    // ── Title + instruction ───────────────────────────────────────────────
    this.add.text(GAME_WIDTH / 2, 52, '💃 워킹 타이밍', {
      fontFamily: Theme.font.family, fontSize: '32px', fontStyle: '800', color: '#fff',
    }).setOrigin(0.5);
    this.add.text(GAME_WIDTH / 2, 100, '게이지가 꽉 찰 때 SPACE 또는 탭!', {
      fontFamily: Theme.font.family, fontSize: '16px', color: '#ffabd0',
    }).setOrigin(0.5);

    // ── Score chip ────────────────────────────────────────────────────────
    const sG = this.add.graphics();
    sG.fillStyle(0x2a1a44, 0.85); sG.fillRoundedRect(16, 10, 160, 44, 12);
    this.scoreText = this.add.text(96, 32, '0', {
      fontFamily: Theme.font.family, fontSize: '28px', fontStyle: '900', color: '#ffd95c',
    }).setOrigin(0.5);

    // ── Timer chip ────────────────────────────────────────────────────────
    const tG = this.add.graphics();
    tG.fillStyle(0x2a1a44, 0.85); tG.fillRoundedRect(GAME_WIDTH - 176, 10, 160, 44, 12);
    this.timerText = this.add.text(GAME_WIDTH - 96, 32, '30s', {
      fontFamily: Theme.font.family, fontSize: '20px', fontStyle: '800', color: '#ff6fa1',
    }).setOrigin(0.5);

    // ── Time progress bar ─────────────────────────────────────────────────
    const barX = 200, barY = 18, barW = GAME_WIDTH - 400;
    this.add.rectangle(barX + barW / 2, barY, barW, 12, 0x333355).setOrigin(0.5);
    this.progressBar = this.add.rectangle(barX, barY, barW, 12, 0x44ee88).setOrigin(0, 0.5);

    // ── Combo text ────────────────────────────────────────────────────────
    this.comboText = this.add.text(GAME_WIDTH / 2, 148, '', {
      fontFamily: Theme.font.family, fontSize: '22px', fontStyle: '900', color: '#ffd95c',
    }).setOrigin(0.5);

    // ── Character ─────────────────────────────────────────────────────────
    const hairId = GameState.data.currentEquipped.hair;
    const fullKey = hairId ? `char_full_${hairId}` : null;
    const useKey = fullKey && this.textures.exists(fullKey) ? fullKey : 'character_bald';
    if (this.textures.exists(useKey)) {
      this.characterSprite = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, useKey).setScale(1.0);
    } else {
      const equipped = OutfitRenderer.resolveEquipped(GameState.data.currentEquipped, OUTFITS_BY_ID);
      const svg = OutfitRenderer.renderCharacterSVG(equipped);
      const key = 'walking_character_main';
      await svgStringToTexture(this, key, svg);
      this.characterSprite = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, key).setScale(2);
    }
    CharacterAnimator.walk(this, this.characterSprite);

    // ── Beat gauge (main visual cue) ──────────────────────────────────────
    // Background track
    const gaugeBgG = this.add.graphics();
    gaugeBgG.fillStyle(0x2a1a44, 1);
    gaugeBgG.fillRoundedRect(GAUGE_X - 4, GAUGE_Y - 4, GAUGE_W + 8, GAUGE_H + 8, 8);
    gaugeBgG.lineStyle(2, 0x9b6bff, 1);
    gaugeBgG.strokeRoundedRect(GAUGE_X - 4, GAUGE_Y - 4, GAUGE_W + 8, GAUGE_H + 8, 8);

    this.add.rectangle(GAUGE_X + GAUGE_W / 2, GAUGE_Y + GAUGE_H / 2, GAUGE_W, GAUGE_H, 0x110a22);
    this.gaugeBar = this.add.rectangle(GAUGE_X, GAUGE_Y + GAUGE_H / 2, 0, GAUGE_H, 0xff6fa1).setOrigin(0, 0.5);

    // "NOW!" flash at right end
    this.beatFlash = this.add.rectangle(GAUGE_X + GAUGE_W, GAUGE_Y + GAUGE_H / 2, 8, GAUGE_H + 16, 0xffd95c, 0);

    // Gauge label
    this.add.text(GAUGE_X + GAUGE_W / 2, GAUGE_Y - 20, '👆 꽉 찰 때 탭!', {
      fontFamily: Theme.font.family, fontSize: '14px', fontStyle: '700', color: '#ffabd0',
    }).setOrigin(0.5);

    // Countdown text (large "NOW!")
    this.countdownText = this.add.text(GAUGE_X + GAUGE_W + 60, GAUGE_Y + GAUGE_H / 2, '', {
      fontFamily: Theme.font.family, fontSize: '28px', fontStyle: '900', color: '#ffd95c',
    }).setOrigin(0, 0.5);

    // ── Tutorial overlay ──────────────────────────────────────────────────
    if (this.showTutorial) {
      this._showTutorial();
    }

    this.input.on('pointerdown', () => this.tryHit());
    this.input.keyboard?.on('keydown-SPACE', () => this.tryHit());

    new Button(this, 100, GAME_HEIGHT - 50, '← 종료', () => this.end(), { width: 120, height: 48, style: 'secondary' });

    this.gameTimer = this.time.addEvent({ delay: 16, loop: true, callback: () => this.tick() });
  }

  private _showTutorial(): void {
    this.gameTimer?.paused && (this.gameTimer.paused = true);
    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.75);
    overlay.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    overlay.setDepth(500);

    const tutBox = this.add.graphics().setDepth(501);
    tutBox.fillStyle(0x2a1a44, 1);
    tutBox.fillRoundedRect(GAME_WIDTH / 2 - 350, GAME_HEIGHT / 2 - 160, 700, 320, 20);
    tutBox.lineStyle(3, 0xff6fa1, 1);
    tutBox.strokeRoundedRect(GAME_WIDTH / 2 - 350, GAME_HEIGHT / 2 - 160, 700, 320, 20);

    const lines = [
      '💃 워킹 타이밍 게임 방법',
      '',
      '화면 하단의 게이지가 꽉 찰 때',
      'SPACE 또는 화면을 탭하세요!',
      '',
      '게이지가 꽉 찰 때 탭하면 "PERFECT!"',
      '살짝 빗나가면 "GOOD", 놓치면 콤보가 끊겨요.',
      '',
      '준비가 되면 탭해서 시작!',
    ];
    lines.forEach((line, i) => {
      this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 120 + i * 38, line, {
        fontFamily: Theme.font.family,
        fontSize: i === 0 ? '24px' : '16px',
        fontStyle: i === 0 ? '900' : '700',
        color: i === 0 ? '#ffd95c' : '#ffffff',
      }).setOrigin(0.5).setDepth(502);
    });

    // Dismiss on tap
    const closeHit = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0).setDepth(503);
    closeHit.setInteractive({ useHandCursor: true });
    closeHit.once('pointerdown', () => {
      overlay.destroy();
      tutBox.destroy();
      closeHit.destroy();
      this.showTutorial = false;
      this.nextBeatAt = this.time.now + BEAT_INTERVAL;
    });
  }

  private tick(): void {
    if (this.showTutorial) return;

    this.timeLeft -= 16;
    const secs = Math.ceil(this.timeLeft / 1000);
    this.timerText.setText(`${secs}s`);
    const ratio = Math.max(0, this.timeLeft / ROUND_MS);
    const barW = GAME_WIDTH - 400;
    this.progressBar.setDisplaySize(barW * ratio, 12);
    const r = Math.min(255, Math.floor((1 - ratio) * 510));
    const g = Math.min(255, Math.floor(ratio * 510));
    this.progressBar.setFillStyle(Phaser.Display.Color.GetColor(r, g, 50));
    if (this.timeLeft <= 0) { this.end(); return; }

    const timeUntilBeat = this.nextBeatAt - this.time.now;

    if (timeUntilBeat < 0) {
      // Missed beat
      this.combo = 0;
      this.comboText.setText('');
      this.nextBeatAt = this.time.now + BEAT_INTERVAL;
      // Red flash on gauge
      this.gaugeBar.setFillStyle(0xff3333);
      this.time.delayedCall(200, () => { if (this.gaugeBar.active) this.gaugeBar.setFillStyle(0xff6fa1); });
    } else {
      // Fill gauge: 0 when far away, 1 when at beat
      const fillRatio = Math.min(1, 1 - timeUntilBeat / BEAT_INTERVAL);
      const fillW = GAUGE_W * fillRatio;
      this.gaugeBar.setDisplaySize(fillW, GAUGE_H);

      // Color transition: blue → pink → yellow → flash white at 100%
      if (fillRatio < 0.7) {
        const t = fillRatio / 0.7;
        const rb = Math.floor(t * 255);
        this.gaugeBar.setFillStyle(Phaser.Display.Color.GetColor(rb, 50, 200));
      } else if (fillRatio < 0.95) {
        const t = (fillRatio - 0.7) / 0.25;
        this.gaugeBar.setFillStyle(Phaser.Display.Color.GetColor(255, Math.floor(t * 100), 100));
      } else {
        // Near perfect: bright yellow pulse
        const pulse = Math.sin(this.time.now * 0.04) > 0;
        this.gaugeBar.setFillStyle(pulse ? 0xffd95c : 0xffffff);
        this.beatFlash.setFillStyle(0xffd95c, 0.7);
      }

      // Beat flash fades when not near perfect
      if (fillRatio < 0.9) {
        this.beatFlash.setFillStyle(0xffd95c, 0);
      }

      // Countdown label
      if (timeUntilBeat < 300) {
        this.countdownText.setText('NOW!').setColor('#ffd95c');
      } else if (timeUntilBeat < 500) {
        this.countdownText.setText('GO!').setColor('#ff6fa1');
      } else {
        this.countdownText.setText('');
      }
    }
  }

  private tryHit(): void {
    if (this.ended || this.showTutorial) return;
    const dist = Math.abs(this.nextBeatAt - this.time.now);
    let pts = 0; let label = '';
    if (dist < PERFECT_WIN) { pts = 30; label = 'PERFECT! 👑'; }
    else if (dist < GOOD_WIN) { pts = 12; label = 'GOOD 💃'; }
    if (pts > 0) {
      AudioManager.playSfx('beat_hit');
      this.score += pts + this.combo;
      this.combo++;
      this.scoreText.setText(`${this.score}`);
      this.comboText.setText(`${this.combo} 콤보 ${label}`);
      this.tweens.add({ targets: this.characterSprite!, scaleX: 4.4, scaleY: 4.4, duration: 100, yoyo: true });
      const fx = this.add.text(GAME_WIDTH / 2, GAUGE_Y - 60, label, {
        fontFamily: 'Pretendard Variable', fontSize: '36px', fontStyle: '900',
        color: pts === 30 ? '#ffd95c' : '#ff6fa1',
        stroke: '#000000', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(200);
      this.tweens.add({ targets: fx, alpha: 0, y: fx.y - 60, duration: 800, onComplete: () => fx.destroy() });
      // Sparkle on perfect
      if (pts === 30) {
        for (let i = 0; i < 6; i++) {
          const spark = this.add.text(
            GAME_WIDTH / 2 + (Math.random() - 0.5) * 200,
            GAME_HEIGHT / 2 + (Math.random() - 0.5) * 100,
            '✨', { fontSize: '22px' },
          ).setOrigin(0.5).setDepth(200);
          this.tweens.add({ targets: spark, alpha: 0, scale: 2, duration: 600, delay: i * 60, onComplete: () => spark.destroy() });
        }
      }
      this.nextBeatAt = this.time.now + BEAT_INTERVAL;
      // Reset gauge
      this.gaugeBar.setDisplaySize(0, GAUGE_H);
      this.gaugeBar.setFillStyle(0xff6fa1);
    } else {
      // Miss — minor penalty feedback
      this.gaugeBar.setFillStyle(0xff3333);
      this.time.delayedCall(150, () => { if (this.gaugeBar.active) this.gaugeBar.setFillStyle(0xff6fa1); });
    }
  }

  private end(): void {
    if (this.ended) return; this.ended = true;
    this.gameTimer.remove();
    const reward = MiniGameRunner.reportResult('walkingTiming', this.score);
    if (this.characterSprite) CharacterAnimator.cheer(this, this.characterSprite);
    this.toast.show(`🎉 ${this.score}점! ⭐+${reward.stars}, 💰+${reward.coins}`, 'gold');
    this.time.delayedCall(2000, () => {
      this.cameras.main.fadeOut(200);
      this.cameras.main.once('camerafadeoutcomplete', () => this.scene.start('MiniGameHubScene'));
    });
  }
}
